import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  Stack,
  Tooltip,
  Avatar,
  FormControlLabel,
  Radio,
  Checkbox,
  InputAdornment,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  IconButton,
  Popover,
} from "@mui/material";
import {
  Person as UserIcon,
  Book as BookOpenIcon,
  Badge as SquareUserIcon,
  Search as SearchIcon,
  Label as TagIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Label as LabelIcon,
  MoreHoriz as MoreHorizIcon,
  Close as CloseIcon,
  WorkspacePremium as ProfileIcon,
  Category as SpecialtyIcon,
  GroupWork as GroupIcon,
} from "@mui/icons-material";
import LabelsService from "../../../services/LabelsService";
import CoursesService from "../../../services/CoursesService";

const RegisterForm = ({ selectedUser }) => {
  const [formData, setFormData] = useState({
    nombre: "",
    cedula: "",
    correo: "",
    estadoCurso: true,
  });

  const [cursoSearch, setCursoSearch] = useState("");
  const [selectedCursos, setSelectedCursos] = useState([]);
  const [profileSearch, setProfileSearch] = useState("");
  const [labelSearch, setLabelSearch] = useState("");

  const [labels, setLabels] = useState([]);
  const [labelGroups, setLabelGroups] = useState([]);
  const [labelGroupsLoading, setLabelGroupsLoading] = useState(false);
  const [labelGroupDetails, setLabelGroupDetails] = useState({});
  const [labelGroupAssigning, setLabelGroupAssigning] = useState({});
  const [labelGroupMessage, setLabelGroupMessage] = useState(null);
  const [audienceTargets, setAudienceTargets] = useState({});
  const [userLabels, setUserLabels] = useState([]);
  const [courses, setCourses] = useState([]);
  const [userCourses, setUserCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [labelsPopoverAnchor, setLabelsPopoverAnchor] = useState(null);
  const [labelsPopoverData, setLabelsPopoverData] = useState([]);

  const refreshUserLabels = async (userId) => {
    const initialTargets = {};
    if (selectedUser?.labelId) {
      initialTargets[selectedUser.labelId] = true;
    }
    try {
      const etiquetasUsuario = await LabelsService.getUserLabels(userId);
      setUserLabels(etiquetasUsuario);
      etiquetasUsuario.forEach((etiqueta) => {
        initialTargets[etiqueta.labelId] = true;
      });
      setAudienceTargets(initialTargets);
    } catch (error) {
      setAudienceTargets(initialTargets);
    }
  };

  useEffect(() => {
    if (selectedUser) {
      setFormData({
        nombre: selectedUser.name || "",
        cedula: selectedUser.cedula || "",
        correo: "",
      });
      setLabelGroupMessage(null);
      refreshUserLabels(selectedUser.id);

      const fetchUserCourses = async () => {
        try {
          const response = await CoursesService.getUserCourses(selectedUser.id, null);
          setUserCourses(response || []);
        } catch (error) {
          setUserCourses([]);
        }
      };
      fetchUserCourses();
    }
  }, [selectedUser]);

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const response = await LabelsService.getAllLabels();
        setLabels(response || []);
      } catch (error) {
        setLabels([]);
      }
    };
    fetchLabels();
  }, []);

  useEffect(() => {
    const fetchLabelGroups = async () => {
      try {
        setLabelGroupsLoading(true);
        const response = await LabelsService.getLabelGroups();
        setLabelGroups(response || []);
      } catch (error) {
        setLabelGroups([]);
      } finally {
        setLabelGroupsLoading(false);
      }
    };
    fetchLabelGroups();
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await CoursesService.getCourses({}, null);
        setCourses(response || []);
      } catch (error) {
        setCourses([]);
      }
    };
    fetchCourses();
  }, []);

  const handleLabelToggle = (labelId, isChecked) => {
    setAudienceTargets((prev) => ({
      ...prev,
      [labelId]: isChecked,
    }));
  };

  const handleAddLabel = async (labelId) => {
    try {
      setIsLoading(true);
      await LabelsService.saveUserLabel(selectedUser.id, labelId);
      
      setAudienceTargets(prev => ({
        ...prev,
        [labelId]: true
      }));
      
      const etiquetasUsuario = await LabelsService.getUserLabels(selectedUser.id);
      setUserLabels(etiquetasUsuario);
    } catch (error) {
      console.error("Error al agregar la etiqueta:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveLabel = async (labelId) => {
    try {
      setIsLoading(true);
      const userLabel = userLabels.find(ul => ul.labelId === labelId);
      if (userLabel) {
        await LabelsService.deleteUserLabel(userLabel.id);
        
        setAudienceTargets(prev => ({
          ...prev,
          [labelId]: false
        }));
        
        const etiquetasUsuario = await LabelsService.getUserLabels(selectedUser.id);
        setUserLabels(etiquetasUsuario);
      }
    } catch (error) {
      console.error("Error al eliminar la etiqueta:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileToggle = async (labelId, isChecked) => {
    if (!selectedUser?.id) return;

    if (!isChecked) {
      const userLabel = userLabels.find((ul) => ul.labelId === labelId);
      if (!userLabel) {
        handleLabelToggle(labelId, false);
        return;
      }
      try {
        setIsLoading(true);
        await LabelsService.deleteUserLabel(userLabel.id);
        await refreshUserLabels(selectedUser.id);
      } catch (error) {
        console.error("Error al eliminar el perfil:", error);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    try {
      setIsLoading(true);
      const profileLabelIds = new Set(profileLabels.map((label) => label.id));
      const existingProfileUserLabels = userLabels.filter((ul) => profileLabelIds.has(ul.labelId));

      if (existingProfileUserLabels.length > 0) {
        await Promise.all(
          existingProfileUserLabels.map((ul) => LabelsService.deleteUserLabel(ul.id))
        );
      }

      await LabelsService.saveUserLabel(selectedUser.id, labelId);
      await refreshUserLabels(selectedUser.id);
    } catch (error) {
      console.error("Error al asignar el perfil:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCursoToggle = async (courseId, isChecked) => {
    try {
      setLoadingCourses(prev => ({ ...prev, [courseId]: true }));
      
      if (isChecked) {
        await CoursesService.assignDirectly(selectedUser.id, courseId, null);
      } else {
        await CoursesService.removeUserCourse(selectedUser.id, courseId, null);
      }
      
      const response = await CoursesService.getUserCourses(selectedUser.id, null);
      setUserCourses(response || []);
    } catch (error) {
      console.error(`Error al ${isChecked ? 'asignar' : 'remover'} el curso:`, error);
    } finally {
      setLoadingCourses(prev => ({ ...prev, [courseId]: false }));
    }
  };

  const handleToggleLabelGroupDetails = async (groupId) => {
    const currentDetails = labelGroupDetails[groupId];
    if (currentDetails?.open) {
      setLabelGroupDetails((prev) => ({
        ...prev,
        [groupId]: {
          ...currentDetails,
          open: false,
        },
      }));
      return;
    }

    if (currentDetails?.data) {
      setLabelGroupDetails((prev) => ({
        ...prev,
        [groupId]: {
          ...currentDetails,
          open: true,
        },
      }));
      return;
    }

    setLabelGroupDetails((prev) => ({
      ...prev,
      [groupId]: {
        ...currentDetails,
        open: true,
        loading: true,
      },
    }));

    try {
      const data = await LabelsService.getLabelGroupWithLabels(groupId);
      setLabelGroupDetails((prev) => ({
        ...prev,
        [groupId]: {
          open: true,
          loading: false,
          data,
        },
      }));
    } catch (error) {
      setLabelGroupDetails((prev) => ({
        ...prev,
        [groupId]: {
          open: true,
          loading: false,
          error: true,
        },
      }));
    }
  };

  const handleAssignLabelGroup = async (group) => {
    if (!selectedUser?.id) return;

    const confirmed = window.confirm(
      `¿Confirmas asignar el grupo "${group.name}" al usuario ${selectedUser.name || 'seleccionado'}?`
    );
    if (!confirmed) return;

    try {
      setLabelGroupAssigning((prev) => ({ ...prev, [group.id]: true }));
      await LabelsService.assignLabelGroups(selectedUser.id, [group.id]);
      await refreshUserLabels(selectedUser.id);
      setLabelGroupMessage({
        type: "success",
        text: `Grupo "${group.name}" asignado correctamente a ${selectedUser.name || "el usuario"}.`,
      });
    } catch (error) {
      console.error("Error al asignar el grupo de etiquetas:", error);
      setLabelGroupMessage({
        type: "error",
        text: `No se pudo asignar el grupo "${group.name}". Intenta de nuevo.`,
      });
    } finally {
      setLabelGroupAssigning((prev) => ({ ...prev, [group.id]: false }));
    }
  };

  const cleanText = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const cursosFiltrados = courses.filter((curso) =>
    cleanText(curso.title).toLowerCase().includes(cursoSearch.toLowerCase())
  );

  const userCourseIds = userCourses.map((c) => c.id);
  const profileLabels = labels.filter((label) => label.isProfile);
  const nonProfileLabels = labels.filter((label) => !label.isProfile);
  const profileLabelsFiltrados = profileLabels.filter((label) =>
    (label.name || "").toLowerCase().includes(profileSearch.toLowerCase())
  );
  const nonProfileLabelsFiltrados = nonProfileLabels.filter((label) =>
    (label.name || "").toLowerCase().includes(labelSearch.toLowerCase())
  );

  // Función para renderizar etiquetas con límite y tooltip
  const renderSelectedLabels = (labels, maxVisible = 3) => {
    if (!labels || labels.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 2.5 }}>
          <LabelIcon sx={{ fontSize: 28, color: 'rgba(77, 171, 164, 0.4)', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No hay especialidades asignadas
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Usa el buscador para agregar
          </Typography>
        </Box>
      );
    }

    if (labels.length <= maxVisible) {
      return (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {labels.map((label) => (
            <Chip
              key={label.id}
              label={label.name}
              size="small"
              color="primary"
              variant="filled"
              onDelete={() => handleRemoveLabel(label.id)}
              deleteIcon={<RemoveIcon sx={{ fontSize: 16 }} />}
              sx={{
                fontWeight: 600,
                backgroundColor: '#4DABA4',
                boxShadow: '0 2px 6px rgba(77, 171, 164, 0.3)',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(77, 171, 164, 0.4)',
                  backgroundColor: '#3e958e',
                },
              }}
            />
          ))}
        </Stack>
      );
    }

    const visibleLabels = labels.slice(0, maxVisible);
    const hiddenLabels = labels.slice(maxVisible);
    const hiddenCount = hiddenLabels.length;

    const handleMoreClick = (event) => {
      setLabelsPopoverAnchor(event.currentTarget);
      setLabelsPopoverData(hiddenLabels);
    };

    const handleClosePopover = () => {
      setLabelsPopoverAnchor(null);
      setLabelsPopoverData([]);
    };

    return (
      <>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        {visibleLabels.map((label) => (
          <Chip
            key={label.id}
            label={label.name}
            size="small"
            color="primary"
            variant="filled"
            onDelete={() => handleRemoveLabel(label.id)}
            deleteIcon={<RemoveIcon sx={{ fontSize: 16 }} />}
            sx={{
              fontWeight: 600,
              backgroundColor: '#4DABA4',
              boxShadow: '0 2px 6px rgba(77, 171, 164, 0.3)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(77, 171, 164, 0.4)',
                backgroundColor: '#3e958e',
              },
            }}
          />
        ))}
          <Tooltip title={`Ver ${hiddenCount} especialidad${hiddenCount !== 1 ? 'es' : ''} más`}>
            <Chip
              label={`+${hiddenCount}`}
              size="small"
              variant="outlined"
              color="default"
              icon={<MoreHorizIcon />}
              onClick={handleMoreClick}
              sx={{
                backgroundColor: 'rgba(156, 163, 175, 0.1)',
                borderColor: '#d1d5db',
                color: '#6b7280',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(156, 163, 175, 0.2)',
                },
              }}
            />
          </Tooltip>
        </Stack>
        <Popover
          open={Boolean(labelsPopoverAnchor)}
          anchorEl={labelsPopoverAnchor}
          onClose={handleClosePopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              mt: 1.5,
              p: 2,
              minWidth: 220,
              maxWidth: 320,
              maxHeight: 300,
              overflow: 'auto',
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              border: '1px solid #e2e8f0',
            },
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#374151' }}>
            Especialidades adicionales
          </Typography>
          <Stack spacing={1} direction="row" flexWrap="wrap" useFlexGap>
            {labelsPopoverData.map((label) => (
              <Chip
                key={label.id}
                label={label.name}
                size="small"
                color="primary"
                variant="filled"
                onDelete={() => {
                  handleRemoveLabel(label.id);
                  handleClosePopover();
                }}
                deleteIcon={<RemoveIcon sx={{ fontSize: 16 }} />}
                sx={{
                  fontWeight: 600,
                  backgroundColor: '#4DABA4',
                  '&:hover': { backgroundColor: '#3e958e' },
                }}
              />
            ))}
          </Stack>
        </Popover>
      </>
    );
  };

  const primaryColor = '#4DABA4';
  const primaryLight = 'rgba(77, 171, 164, 0.08)';

  if (!selectedUser) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          px: 3,
          background: 'linear-gradient(180deg, rgba(77, 171, 164, 0.03) 0%, transparent 100%)',
          borderRadius: 4,
          border: '2px dashed rgba(77, 171, 164, 0.3)',
          mx: 3,
        }}
      >
        <Box
          sx={{
            width: 100,
            height: 100,
            borderRadius: '50%',
            bgcolor: primaryLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <PersonIcon sx={{ fontSize: 48, color: primaryColor }} />
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
          Selecciona un usuario para editar
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, opacity: 0.8 }}>
          Elige un usuario de la lista para gestionar su perfil, especialidades y cursos
        </Typography>
      </Box>
    );
  }

  const scrollContainerStyles = {
    maxHeight: 280,
    overflowY: 'auto',
    pr: 0.5,
    '&::-webkit-scrollbar': { width: 6 },
    '&::-webkit-scrollbar-track': { backgroundColor: '#f1f5f9', borderRadius: 3 },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#cbd5e1',
      borderRadius: 3,
      '&:hover': { backgroundColor: '#94a3b8' },
    },
  };

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      }}
    >
      <Box sx={{ flex: 1, px: 3, py: 3, mx: 'auto', maxWidth: '1600px', width: '100%' }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 'calc(100vh - 48px)',
          }}
        >
          {/* Header con información del usuario */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #4DABA4 0%, #3e958e 50%, #2d7a73 100%)',
              p: 4,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -80,
                right: -80,
                width: 240,
                height: 240,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.08)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -40,
                left: -40,
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={3} position="relative" zIndex={1}>
              <Avatar
                sx={{
                  width: 88,
                  height: 88,
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  fontSize: '2.25rem',
                  fontWeight: 700,
                  border: '4px solid rgba(255, 255, 255, 0.4)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                }}
              >
                {selectedUser.name ? selectedUser.name.charAt(0).toUpperCase() : '?'}
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                  {selectedUser.name || 'Usuario'}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SquareUserIcon sx={{ fontSize: 18 }} />
                  Cédula: {selectedUser.cedula || 'N/A'}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Chip
                    label={`${userLabels.length} etiqueta${userLabels.length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                  />
                  <Chip
                    label={`${userCourseIds.length} curso${userCourseIds.length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                  />
                </Stack>
              </Box>
            </Stack>
          </Box>

          {/* Información Personal */}
          <Box sx={{ p: 3, px: 4, borderBottom: '1px solid #e2e8f0', flexShrink: 0, bgcolor: 'rgba(248, 250, 252, 0.5)' }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #4DABA4 0%, #3e958e 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 14px rgba(77, 171, 164, 0.35)',
                }}
              >
                <UserIcon sx={{ fontSize: 22, color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937' }}>
                  Información Personal
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  Datos básicos del usuario
                </Typography>
              </Box>
            </Stack>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre Completo"
                  value={formData.nombre}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <UserIcon sx={{ color: '#9ca3af' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f9fafb',
                      '&.Mui-disabled': {
                        backgroundColor: '#f3f4f6',
                      },
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Número de Cédula"
                  value={formData.cedula}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SquareUserIcon sx={{ color: '#9ca3af' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f9fafb',
                      '&.Mui-disabled': {
                        backgroundColor: '#f3f4f6',
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Clasificación Profesional */}
          <Box sx={{ p: 3, px: 4, borderBottom: '1px solid #e2e8f0', flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #4DABA4 0%, #3e958e 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 14px rgba(77, 171, 164, 0.35)',
                }}
              >
                <TagIcon sx={{ fontSize: 22, color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937' }}>
                  Clasificación Profesional
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  Perfiles, especialidades y grupos
                </Typography>
              </Box>
            </Stack>
            
            <Grid container spacing={4} alignItems="stretch" sx={{ minHeight: 320 }}>
              {/* Perfiles */}
              <Grid item xs={12} lg={6} sx={{ display: 'flex' }}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 3,
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      borderColor: 'rgba(77, 171, 164, 0.4)',
                      boxShadow: '0 8px 24px rgba(77, 171, 164, 0.08)',
                    },
                  }}
                >
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                      <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: 'rgba(77, 171, 164, 0.1)' }}>
                        <ProfileIcon sx={{ fontSize: 20, color: '#4DABA4' }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                        Perfil Profesional
                      </Typography>
                    </Stack>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Buscar perfil..."
                      value={profileSearch}
                      onChange={(e) => setProfileSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                        endAdornment: profileSearch && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setProfileSearch('')} edge="end">
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#f9fafb',
                          transition: 'all 0.2s',
                          '&:hover': { backgroundColor: '#f3f4f6' },
                          '&.Mui-focused': { backgroundColor: 'white', boxShadow: '0 0 0 2px rgba(77, 171, 164, 0.2)' },
                        },
                      }}
                    />
                    <Box sx={scrollContainerStyles}>
                      <Stack spacing={1.5}>
                        {profileLabelsFiltrados.map((label) => {
                        const isChecked = !!audienceTargets[label.id];
                        return (
                          <FormControlLabel
                            key={label.id}
                            control={
                              <Radio
                                checked={isChecked}
                                onChange={(e) => handleProfileToggle(label.id, e.target.checked)}
                                sx={{
                                  color: '#4DABA4',
                                  '&.Mui-checked': {
                                    color: '#4DABA4',
                                  },
                                }}
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={label.name}
                                  size="small"
                                  color={isChecked ? "success" : "default"}
                                  variant={isChecked ? "filled" : "outlined"}
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                  }}
                                />
                              </Box>
                            }
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: isChecked ? 'rgba(77, 171, 164, 0.3)' : '#e2e8f0',
                              backgroundColor: isChecked ? 'rgba(77, 171, 164, 0.06)' : 'transparent',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: isChecked ? 'rgba(77, 171, 164, 0.1)' : '#f8fafc',
                              },
                            }}
                          />
                        );
                      })}
                      </Stack>
                      {profileLabelsFiltrados.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <ProfileIcon sx={{ fontSize: 36, color: 'rgba(77, 171, 164, 0.3)', mb: 1.5 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {profileSearch ? 'No hay perfiles que coincidan con la búsqueda' : 'No hay perfiles disponibles'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Especialidades */}
              <Grid item xs={12} lg={6} sx={{ display: 'flex' }}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 3,
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      borderColor: 'rgba(77, 171, 164, 0.4)',
                      boxShadow: '0 8px 24px rgba(77, 171, 164, 0.08)',
                    },
                  }}
                >
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                      <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: 'rgba(77, 171, 164, 0.1)' }}>
                        <SpecialtyIcon sx={{ fontSize: 20, color: '#4DABA4' }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                        Especialidades
                      </Typography>
                    </Stack>

                    {/* Etiquetas Seleccionadas */}
                    <Box sx={{ mb: 2, flexShrink: 0 }}>
                      <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500, color: '#6b7280' }}>
                        Especialidades Asignadas
                      </Typography>
                      <Box
                        sx={{
                          minHeight: 72,
                          maxHeight: 140,
                          p: 2,
                          border: '1px solid #e2e8f0',
                          borderRadius: 2,
                          backgroundColor: '#f8fafc',
                          overflowY: 'auto',
                          pr: 0.5,
                          '&::-webkit-scrollbar': { width: 6 },
                          '&::-webkit-scrollbar-track': { backgroundColor: '#f1f5f9', borderRadius: 3 },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#cbd5e1',
                            borderRadius: 3,
                            '&:hover': { backgroundColor: '#94a3b8' },
                          },
                        }}
                      >
                        {renderSelectedLabels(
                          nonProfileLabels.filter(label => audienceTargets[label.id])
                        )}
                      </Box>
                    </Box>

                    {/* Buscador y Especialidades Disponibles */}
                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500, color: '#6b7280' }}>
                        Agregar Especialidades
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Buscar especialidad..."
                        value={labelSearch}
                        onChange={(e) => setLabelSearch(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          endAdornment: labelSearch && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setLabelSearch('')} edge="end">
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 1.5,
                          flexShrink: 0,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#f9fafb',
                            transition: 'all 0.2s',
                            '&:hover': { backgroundColor: '#f3f4f6' },
                            '&.Mui-focused': { backgroundColor: 'white', boxShadow: '0 0 0 2px rgba(77, 171, 164, 0.2)' },
                          },
                        }}
                      />
                      <Box sx={{ ...scrollContainerStyles, flex: 1, minHeight: 100 }}>
                        <Stack spacing={1}>
                        {nonProfileLabelsFiltrados
                          .filter(label => !audienceTargets[label.id])
                          .map((label) => (
                            <Button
                              key={label.id}
                              variant="outlined"
                              size="small"
                              onClick={() => handleAddLabel(label.id)}
                              disabled={isLoading}
                              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                              sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                fontWeight: 500,
                                borderColor: '#e2e8f0',
                                color: '#374151',
                                borderRadius: 2,
                                py: 1.25,
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#4DABA4',
                                  backgroundColor: 'rgba(77, 171, 164, 0.06)',
                                  transform: 'translateX(4px)',
                                },
                              }}
                            >
                              {label.name}
                            </Button>
                          ))}
                        {nonProfileLabelsFiltrados.filter(label => !audienceTargets[label.id]).length === 0 && (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'rgba(16, 185, 129, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
                              <CheckCircleIcon sx={{ fontSize: 28, color: '#10b981' }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {labelSearch
                                ? 'No hay especialidades que coincidan con la búsqueda'
                                : 'Todas las especialidades han sido asignadas'}
                            </Typography>
                          </Box>
                        )}
                        </Stack>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Grupos de etiquetas */}
              <Grid item xs={12}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 3,
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      borderColor: 'rgba(77, 171, 164, 0.4)',
                      boxShadow: '0 8px 24px rgba(77, 171, 164, 0.08)',
                    },
                  }}
                >
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                      <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: 'rgba(77, 171, 164, 0.1)' }}>
                        <GroupIcon sx={{ fontSize: 20, color: '#4DABA4' }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                        Grupos de etiquetas
                      </Typography>
                    </Stack>
                    {labelGroupMessage && (
                      <Alert
                        severity={labelGroupMessage.type}
                        sx={{ mb: 2, borderRadius: 2 }}
                        onClose={() => setLabelGroupMessage(null)}
                      >
                        {labelGroupMessage.text}
                      </Alert>
                    )}

                    {labelGroupsLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={20} sx={{ color: '#4DABA4' }} />
                        <Typography variant="body2" color="text.secondary">
                          Cargando grupos de etiquetas...
                        </Typography>
                      </Box>
                    ) : labelGroups.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          No hay grupos de etiquetas disponibles
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ ...scrollContainerStyles, maxHeight: 320 }}>
                        <Stack spacing={2}>
                        {labelGroups.map((group) => {
                          const details = labelGroupDetails[group.id];
                          const labelsInGroup = details?.data?.labels || [];

                          return (
                            <Box
                              key={group.id}
                              sx={{
                                p: 2,
                                border: '1px solid #e2e8f0',
                                borderRadius: 2,
                                backgroundColor: '#ffffff',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: 'rgba(77, 171, 164, 0.3)',
                                  backgroundColor: 'rgba(248, 250, 252, 0.8)',
                                },
                              }}
                            >
                              <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                                spacing={2}
                              >
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#111827' }}>
                                    {group.name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                    {group.description || 'Sin descripción'}
                                  </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleToggleLabelGroupDetails(group.id)}
                                    disabled={details?.loading}
                                    sx={{
                                      textTransform: 'none',
                                      borderColor: '#d1d5db',
                                      color: '#374151',
                                      '&:hover': {
                                        borderColor: '#4DABA4',
                                        backgroundColor: 'rgba(77, 171, 164, 0.05)',
                                      },
                                    }}
                                  >
                                    {details?.open ? "Ocultar etiquetas" : "Ver etiquetas"}
                                  </Button>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleAssignLabelGroup(group)}
                                    disabled={labelGroupAssigning[group.id]}
                                    sx={{
                                      textTransform: 'none',
                                      backgroundColor: '#4DABA4',
                                      '&:hover': {
                                        backgroundColor: '#3e958e',
                                      },
                                    }}
                                  >
                                    {labelGroupAssigning[group.id] ? "Asignando..." : "Asignar"}
                                  </Button>
                                </Stack>
                              </Stack>

                              {details?.open && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    backgroundColor: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                  }}
                                >
                                  {details?.loading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <CircularProgress size={16} sx={{ color: '#4DABA4' }} />
                                      <Typography variant="caption" color="text.secondary">
                                        Cargando etiquetas...
                                      </Typography>
                                    </Box>
                                  ) : details?.error ? (
                                    <Typography variant="caption" color="error">
                                      No se pudieron cargar las etiquetas del grupo.
                                    </Typography>
                                  ) : labelsInGroup.length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">
                                      Este grupo no tiene etiquetas asignadas.
                                    </Typography>
                                  ) : (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                      {labelsInGroup.map((label, index) => (
                                        <Chip
                                          key={`${label.name}-${index}`}
                                          label={label.name}
                                          size="small"
                                          color={label.isProfile ? "success" : "primary"}
                                          variant="outlined"
                                        />
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                        </Stack>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Asignación de Cursos */}
          <Box sx={{ p: 3, px: 4, flexShrink: 0, bgcolor: 'rgba(248, 250, 252, 0.5)' }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #4DABA4 0%, #3e958e 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 14px rgba(77, 171, 164, 0.35)',
                }}
              >
                <BookOpenIcon sx={{ fontSize: 22, color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937' }}>
                  Matricular Cursos
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  Asignar o remover cursos del usuario
                </Typography>
              </Box>
            </Stack>
            
            {/* Búsqueda de cursos */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Buscar curso..."
                value={cursoSearch}
                onChange={(e) => setCursoSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#9ca3af' }} />
                    </InputAdornment>
                  ),
                  endAdornment: cursoSearch && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setCursoSearch('')} edge="end">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'white',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.2s',
                    '&:hover': { boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' },
                    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(77, 171, 164, 0.2)' },
                  },
                }}
              />
            </Box>
            
            {/* Lista de cursos */}
            <Card
              elevation={0}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 3,
                transition: 'all 0.25s ease',
                '&:hover': {
                  borderColor: 'rgba(77, 171, 164, 0.4)',
                  boxShadow: '0 8px 24px rgba(77, 171, 164, 0.08)',
                },
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    maxHeight: 400,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f1f5f9',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#cbd5e1',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: '#94a3b8',
                      },
                    },
                  }}
                >
                  {cursosFiltrados.map((curso) => {
                    const isLoading = loadingCourses[curso.id];
                    const isSelected = userCourseIds.includes(curso.id);
                    
                    return (
                      <Box
                        key={curso.id}
                        sx={{
                          p: 2,
                          mb: 1,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: isSelected ? 'rgba(77, 171, 164, 0.3)' : '#e2e8f0',
                          backgroundColor: isSelected ? 'rgba(77, 171, 164, 0.06)' : 'white',
                          transition: 'all 0.2s ease',
                          opacity: isLoading ? 0.6 : 1,
                          '&:hover': {
                            backgroundColor: isSelected ? 'rgba(77, 171, 164, 0.1)' : '#f8fafc',
                          },
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isSelected}
                              onChange={(e) => handleCursoToggle(curso.id, e.target.checked)}
                              disabled={isLoading}
                              sx={{
                                color: '#4DABA4',
                                '&.Mui-checked': {
                                  color: '#4DABA4',
                                },
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {cleanText(curso.title)}
                              </Typography>
                              {isLoading && (
                                <CircularProgress size={16} sx={{ color: '#4DABA4' }} />
                              )}
                              {isSelected && (
                                <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
                              )}
                            </Box>
                          }
                          sx={{ width: '100%', m: 0 }}
                        />
                      </Box>
                    );
                  })}
                  
                  {cursosFiltrados.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'rgba(77, 171, 164, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                        <SchoolIcon sx={{ fontSize: 32, color: '#4DABA4' }} />
                      </Box>
                      <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {cursoSearch ? 'No hay cursos que coincidan con la búsqueda' : 'No hay cursos disponibles'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
            
            {/* Resumen de cursos asignados */}
            {userCourseIds.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Alert 
                  severity="success" 
                  icon={<CheckCircleIcon />}
                  sx={{ 
                    borderRadius: 3,
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    color: '#065f46',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {userCourseIds.length} curso{userCourseIds.length !== 1 ? 's' : ''} asignado{userCourseIds.length !== 1 ? 's' : ''} correctamente
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default RegisterForm;